import React, { useEffect, useState, useCallback } from 'react';
import { fetchCatalogProducts } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';
import ProductCard from './ProductCard';
import Pagination from './Pagination';
import CatalogFilters from './CatalogFilters';
import { FaBoxes, FaFilter } from 'react-icons/fa';

const PAGE_SIZE = 20;

// All filter keys empty — the catalog's default unfiltered state. Module-level
// so it's a stable reference (safe for the initial-load effect).
const EMPTY_FILTERS = {
  supplyCategoryCode: '',
  atcCode: '',
  sortimentCode: '',
  migelCode: '',
  genericCode: '',
  consumerCategoryId: '',
  company: '',
};

const CatalogProductList = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters are passed in explicitly (not read from state) so the callback
  // never closes over a stale `filters` value.
  const load = useCallback(async (pageNum, terms, activeFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await fetchCatalogProducts(pageNum, PAGE_SIZE, terms, activeFilters || {});
      if (res.success) {
        setProducts(res.data.products || []);
        setPages(res.data.pages || 1);
        setPage(res.data.page || pageNum);
        setTotal(res.data.total || 0);
      } else {
        setProducts([]);
        setPages(1);
        setPage(1);
        setTotal(0);
      }
    } catch (e) {
      setError('products.catalog.loadError');
      setProducts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(1, '', EMPTY_FILTERS); }, [load]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load(1, search.trim(), filters);
  };

  // A filter changed: merge it in, reset to page 1, and reload with the new set.
  const applyFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setPage(1);
    load(1, search.trim(), next);
  };

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    load(1, search.trim(), EMPTY_FILTERS);
  };

  return (
    <div className="space-y-4">
      {/* Stat cards — same modern set as the shop header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total products */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">{t('products.stats.totalProducts')}</p>
              <p className="text-2xl font-bold text-blue-900">
                {isLoading ? (
                  <span className="inline-block animate-pulse bg-blue-200 h-8 w-16 rounded" />
                ) : (
                  total.toLocaleString()
                )}
              </p>
            </div>
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <FaBoxes className="text-blue-600" />
            </div>
          </div>
        </div>

        {/* Current page */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">{t('products.stats.currentPage')}</p>
              <p className="text-2xl font-bold text-green-900">{page} of {pages}</p>
            </div>
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <FaFilter className="text-green-600" />
            </div>
          </div>
        </div>

        {/* Showing on this page */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">{t('products.stats.showing')}</p>
              <p className="text-2xl font-bold text-purple-900">
                {products.length} {t('products.stats.products')}
              </p>
            </div>
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
              <FaBoxes className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={onSearchSubmit} className="flex gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('products.catalog.searchPlaceholder')}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </form>

      {/* Combinable filter panel — same components & style as the shop */}
      <CatalogFilters filters={filters} onChange={applyFilter} onClearAll={clearAllFilters} />

      {error && <div className="text-red-600 text-sm">{t(error)}</div>}

      {isLoading ? (
        <div className="py-8 text-center text-gray-500">…</div>
      ) : products.length === 0 ? (
        <div className="py-8 text-center text-gray-500">{t('products.catalog.empty')}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {products.map((p) => (
            <div key={p._id} className="relative">
              <span
                className={`absolute top-2 right-2 z-10 inline-flex items-center rounded-full text-xs px-2.5 py-1 ${
                  p.inShop ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {p.inShop ? t('products.badge.inShop') : t('products.badge.catalogOnly')}
              </span>
              <ProductCard product={p} showActions={false} />
            </div>
          ))}
        </div>
      )}

      <Pagination
        currentPage={page}
        totalPages={pages}
        onPageChange={(p) => load(p, search.trim(), filters)}
        totalProducts={total}
        isLoading={isLoading}
        showProductInfo={true}
        productsPerPage={PAGE_SIZE}
      />
    </div>
  );
};

export default CatalogProductList;
